import type { FeatureFlagService } from '@/contracts/FeatureFlagService';
import { FLAG_NAMES } from '@/contracts/FeatureFlagService';

function storageKey(flag: string): string {
	return `VUE_APP_FEATURE_${flag}`;
}

// Not 'true'/'false' — a seeded value must never act as a real override (see
// the constructor comment below for why).
const UNSET = 'default';

// Lets anyone flip a flag in their own browser via devtools (Application >
// Local Storage), no redeploy, no env var needed — independent of whatever
// the wrapped service resolves, and localStorage always wins when set. Key
// is the exact VUE_APP_FEATURE_<NAME> env var name, so there's one name to
// know, not two. Checks explicit 'true'/'false' (not a truthy check) so an
// override can also force a flag OFF where the env default ships it ON, not
// just force it on.
export class LocalOverrideFeatureFlagService implements FeatureFlagService {
	constructor(private readonly wrapped: FeatureFlagService) {
		// Seeds every known flag's key with a neutral placeholder if it isn't
		// already present, so each one shows up in devtools' Local Storage
		// table ready to flip — same discoverability as a Pinia persisted
		// store's always-present fields (e.g. appStore's forceMvr) — instead
		// of requiring someone to already know the exact key name and type it
		// from scratch.
		//
		// Deliberately NOT the resolved true/false: localStorage survives
		// redeploys, and isEnabled() can't tell a seeded snapshot apart from a
		// real manual override. Seeding the actual value would freeze every
		// returning browser at whatever the env resolved to on their first
		// visit — later flipping the env var would then do nothing for them,
		// since their stale seed keeps winning. The neutral placeholder is
		// visible but never matches 'true'/'false', so it always falls
		// through to the live wrapped value below until someone deliberately
		// edits it.
		for (const flag of FLAG_NAMES) {
			const key = storageKey(flag);
			if (localStorage.getItem(key) === null) {
				localStorage.setItem(key, UNSET);
			}
		}
	}

	isEnabled(flag: string): boolean {
		const override = localStorage.getItem(storageKey(flag));

		if (override === 'true') return true;
		if (override === 'false') return false;

		return this.wrapped.isEnabled(flag);
	}
}
